// Models
import { SanitizedMember } from 'models/Member'
import {
  CreateTransactionResponse,
  PaymentGateway,
  TransactionItemTypeAPIv2,
  TransactionRequestBody,
} from 'models/Transaction'

// Redux services
import {
  createRecurringDonationPlan,
  createRecurringDonationPlanTransaction,
} from 'redux/services/recurringDonationPlan'
import { createTransaction } from 'redux/services/transaction'

// Local
import { RootState, TypedDispatch } from 'redux/store'
import getApiv2PaymentType from './getApiv2PaymentType'
import getPaymentSource from './getPaymentSource'
import { FinalizeOrderArgs } from '../types'
import { getDateByFormat } from 'utils/functions/dateUtils'
import { makeOrderContactAnswersPayload } from 'modules/order/state/contact-answers/contact-answers-utils'
import { makeOrderCustomAnswersPayload } from 'modules/order/state/custom-answers/custom-answers-utils'

// Utils
import _ from 'lodash'

const createDraftTransaction = async (
  store: RootState,
  dispatch: TypedDispatch<RootState>,
  member: SanitizedMember,
  args: FinalizeOrderArgs,
): Promise<CreateTransactionResponse> => {
  const { campaign, frequency, currency, baseAmount } = args

  const paymentDetails = store.order.paymentDetails

  const answers = [
    ...makeOrderContactAnswersPayload(store.config, store.order.contactAnswers),
    ...makeOrderCustomAnswersPayload(store.config, store.order.customAnswers),
  ]

  const grossAdjustments = _.omit(store.order.dcf.value, ['fee', 'isChecked', 'totalAmount'])

  const transactionPayload: TransactionRequestBody = {
    billing_address1: store.order.address.value.line1,
    billing_address2: store.order.address.value.line2,
    billing_city: store.order.address.value.city,
    billing_country: store.order.address.value.country,
    billing_first_name: store.order.contact.firstName,
    billing_last_name: store.order.contact.lastName,
    billing_postal_code: store.order.address.value.zipCode,
    billing_state: store.order.address.value.state,
    company_name: store.order.contact.cardCompanyName,
    designation_id: args.designationId,
    hide_amount: store.order.contact.isAmountAnonymous,
    is_anonymous: store.order.contact.isNameAnonymous,
    items: [
      {
        quantity: 1,
        type: TransactionItemTypeAPIv2.DONATION,
        product_name: 'Donation',
        /** Donation amount */
        price: baseAmount,
        /** Final amount (with or without fees depending on dcf) */
        raw_final_price: store.order.dcf.value.totalAmount,
      },
    ],
    is_reprocess: false,
    is_gift_aid: false,
    member_id: member.id,
    member_country: store.order.address.value.country,
    member_email_address: store.order.contact.email,
    member_name: store.order.contact.firstName + ' ' + store.order.contact.lastName,
    member_phone: store.order.contact.phone,
    raw_currency_code: currency,
    payment_type: getApiv2PaymentType(paymentDetails.type),
    payment_method: 'Classy Pay', // All transactions in checkout are processed by classypay api
    payment_gateway: PaymentGateway[paymentDetails.processor] ?? PaymentGateway.CLASSYPAY,
    payment_source: getPaymentSource(paymentDetails.type, store),
    status: 'incomplete',
    answers,
    ...grossAdjustments,
    comment: store.order.comment.value,
    employer_match: store.order.doubleTheDonation.value
      ? {
          employer_id: String(store.order.doubleTheDonation.value.companyId),
          employer_name: store.order.doubleTheDonation.value.companyName,
          employer_search_text: store.order.doubleTheDonation.value.companyName,
        }
      : undefined,
  }

  /**
   * Create an RDP or one time donation based whether a frequency is set.
   */
  if (frequency && frequency !== 'one-time') {
    const rdp = await dispatch(
      createRecurringDonationPlan.initiate({
        /** Required Fields */
        member_id: member.id,
        // TODO: Does raw_donation_amount include fees w/dcf
        raw_donation_amount: baseAmount,
        donation_amount: store.order.dcf.value.totalAmount,
        started_at: getDateByFormat('YYYY-MM-DD') || '',
        recur_until: args.recurringEndDate,
        status: 'draft',
        recurring_donatable_id: String(campaign.campaign_id),
        recurring_donatable_type: 'campaign',

        /** Additional Fields */
        first_name: store.order.contact.firstName,
        last_name: store.order.contact.lastName,
        address: store.order.address.value.line1,
        city: store.order.address.value.city,
        state: store.order.address.value.state,
        country: store.order.address.value.country,
        postal_code: store.order.address.value.zipCode,
        company_name: store.order.contact.cardCompanyName,
        frequency: frequency,
        is_anonymous: store.order.contact.isNameAnonymous,
        hide_amount: store.order.contact.isAmountAnonymous,
        designation_id: args.designationId,
        ...grossAdjustments,
      }),
    ).unwrap()

    return await dispatch(
      createRecurringDonationPlanTransaction.initiate({
        recurringDonationPlanId: rdp.id,
        transaction: transactionPayload,
      }),
    ).unwrap()
  } else {
    return await dispatch(
      createTransaction.initiate({
        campaignId: String(campaign.campaign_id),
        transaction: transactionPayload,
      }),
    ).unwrap()
  }
}

export default createDraftTransaction
